import { useEffect, useRef, useState } from "react";
import { api, API, LOGO_FULL, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TimesheetOwnView from "@/components/TimesheetOwnView";
import LeaveOwnView from "@/components/LeaveOwnView";
import OutstandingNotes from "@/components/OutstandingNotes";
import CatchUpBanner from "@/components/CatchUpBanner";
import {
  CheckCircle2, Circle, FileText, Upload, LogOut, ShieldCheck, ClipboardCheck,
  FileSignature, UserCog, Clock, AlertTriangle, Send, Briefcase, CalendarDays,
  ChevronLeft, ChevronRight, MapPin, DollarSign, CalendarClock, PartyPopper,
} from "lucide-react";
import { toast } from "sonner";

const authFileUrl = (key) => `${API}/contractor/me/documents/${key}/file?auth=${localStorage.getItem("ts_token")}`;
const agreementUrl = () => `${API}/contractor/me/agreement?auth=${localStorage.getItem("ts_token")}`;

function PortalHeader({ user, logout }) {
  return (
    <header className="bg-navy px-5 py-4 flex items-center justify-between">
      <img src={LOGO_FULL} alt="True Seal Caulking" className="h-12 object-contain" />
      <div className="flex items-center gap-3 text-white">
        <span className="text-sm hidden sm:block text-slate-300">{user?.name}</span>
        <button data-testid="contractor-logout" onClick={logout} className="flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2"><LogOut className="w-4 h-4" />Sign out</button>
      </div>
    </header>
  );
}

export default function ContractorPortal() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const load = () => api.get("/contractor/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="min-h-screen bg-navy flex items-center justify-center text-white">Loading…</div>;

  if (data.status === "active") return <ContractorDashboard data={data} onSaved={setData} user={user} logout={logout} />;

  const steps = [
    { done: data.agreement_signed, label: "Agreement Signed" },
    { done: data.profile_complete, label: "Profile Complete" },
    { done: data.documents_complete, label: "Documents Uploaded" },
    { done: data.status === "under_review" || data.status === "active", label: "Submitted" },
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      <PortalHeader user={user} logout={logout} />
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5 pb-20">
        <div>
          <h1 className="font-heading font-bold text-navy text-2xl">Welcome, {data.name.split(" ")[0]}</h1>
          <p className="text-slate-500 text-sm mt-1">Complete your onboarding below. Your account will be activated once True Seal reviews your details.</p>
        </div>

        <StatusBanner data={data} />

        {/* Progress */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {steps.map((s, i) => (
              <div key={i} data-testid={`step-${i}`} className="flex items-center gap-2">
                {s.done ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
                <span className={`text-sm ${s.done ? "text-navy font-medium" : "text-slate-400"}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <AgreementCard data={data} />
        <ProfileCard data={data} onSaved={setData} />
        <DocumentsCard data={data} onSaved={setData} />
        <SubmitCard data={data} onSaved={setData} />
      </div>
    </div>
  );
}

function ContractorDashboard({ data, onSaved, user, logout }) {
  return (
    <div className="min-h-screen bg-slate-100" data-testid="contractor-dashboard">
      <PortalHeader user={user} logout={logout} />
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-5 pb-20">
        <div>
          <h1 className="font-heading font-bold text-navy text-2xl">Welcome back, {data.name.split(" ")[0]}</h1>
          <p className="text-slate-500 text-sm mt-1">Your jobs, calendar and profile.</p>
        </div>
        <SwmsBanner />
        {!data.documents_complete && (
          <a href="#" onClick={(e) => e.preventDefault()} data-testid="docs-outstanding-banner"
             className="block bg-amber-50 border border-amber-300 rounded-2xl p-4">
            <p className="font-heading font-semibold text-amber-800 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Documents outstanding</p>
            <p className="text-sm text-amber-700 mt-0.5">Please complete your required documents in <b>My Profile</b>. We can't process your pay for any worked week until they're all uploaded.</p>
          </a>
        )}
        <OutstandingNotes jobPath={(id) => `/field/job/${id}`} />
        <Tabs defaultValue="jobs">
          <TabsList data-testid="dashboard-tabs">
            <TabsTrigger value="jobs" data-testid="tab-jobs"><Briefcase className="w-4 h-4 mr-1.5" />My Jobs</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarDays className="w-4 h-4 mr-1.5" />Calendar</TabsTrigger>
            <TabsTrigger value="timesheets" data-testid="tab-timesheets"><Clock className="w-4 h-4 mr-1.5" />Timesheets</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability"><CalendarDays className="w-4 h-4 mr-1.5" />Availability</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile"><UserCog className="w-4 h-4 mr-1.5" />My Profile</TabsTrigger>
          </TabsList>
          <TabsContent value="jobs" className="mt-5"><MyJobs /></TabsContent>
          <TabsContent value="calendar" className="mt-5"><CalendarView /></TabsContent>
          <TabsContent value="timesheets" className="mt-5"><TimesheetOwnView /></TabsContent>
          <TabsContent value="availability" className="mt-5"><LeaveOwnView /></TabsContent>
          <TabsContent value="profile" className="mt-5"><MyProfile data={data} onSaved={onSaved} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SwmsBanner() {
  const [swms, setSwms] = useState(null);
  useEffect(() => { api.get("/swms").then((r) => setSwms(r.data)).catch(() => {}); }, []);
  if (!swms?.has_file) return null;
  return (
    <a href={`${API}/swms/file?auth=${localStorage.getItem("ts_token")}`} target="_blank" rel="noreferrer" data-testid="portal-swms-link"
       className="flex items-center justify-between gap-3 bg-navy text-white rounded-xl px-4 py-3 shadow-sm">
      <span className="flex items-center gap-2 text-sm font-medium"><ShieldCheck className="w-5 h-5 text-emerald-400" />Safe Work Method Statement (v{swms.version})</span>
      <span className="text-xs bg-white/15 px-2 py-1 rounded-full">View SWMS</span>
    </a>
  );
}

const JOB_STATUS = {
  scheduled: "bg-blue-100 text-blue-700", in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-100 text-slate-500",
};
const iso = (d) => d.toISOString().slice(0, 10);
const fmtDay = (d) => d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });

function MyJobs() {
  const navigate = useNavigate();
  const [view, setView] = useState("day");
  const [cursor, setCursor] = useState(new Date());
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/contractor/jobs?view=${view}&date=${iso(cursor)}`).then((r) => setJobs(r.data)).finally(() => setLoading(false));
  }, [view, cursor]);

  const shift = (n) => { const d = new Date(cursor); d.setDate(d.getDate() + n * (view === "week" ? 7 : 1)); setCursor(d); };
  const weekLabel = () => {
    const d = new Date(cursor); const monday = new Date(d); monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    return `${fmtDay(monday)} – ${fmtDay(sunday)}`;
  };

  const byDate = jobs.reduce((acc, j) => {
    const days = (j.worker_days && j.worker_days.length) ? j.worker_days : (j.appointment_date ? [j.appointment_date] : []);
    days.forEach((d) => { (acc[d] = acc[d] || []).push(j); });
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <CatchUpBanner jobPath={(id) => `/field/job/${id}`} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="jobs-view-toggle">
          {["day", "week"].map((v) => (
            <button key={v} data-testid={`jobs-view-${v}`} onClick={() => setView(v)} className={`px-3 py-1.5 text-sm rounded-md capitalize ${view === v ? "bg-navy text-white" : "text-slate-600"}`}>{v}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="jobs-prev" onClick={() => shift(-1)} className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium text-navy min-w-[160px] text-center">{view === "day" ? fmtDay(cursor) : weekLabel()}</span>
          <button data-testid="jobs-next" onClick={() => shift(1)} className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"><ChevronRight className="w-4 h-4" /></button>
          <button data-testid="jobs-today" onClick={() => setCursor(new Date())} className="px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50">Today</button>
        </div>
      </div>

      {loading ? <p className="text-slate-400 text-sm">Loading…</p> : jobs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400" data-testid="jobs-empty">
          <Briefcase className="w-9 h-9 mx-auto mb-2 text-slate-300" />No jobs scheduled for this {view}.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.keys(byDate).sort().map((date) => (
            <div key={date}>
              {view === "week" && <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">{fmtDay(new Date(date + "T00:00:00"))}</p>}
              <div className="space-y-2">
                {byDate[date].map((j) => {
                  const sd = (j.schedule_days || []).find((s) => (s.date || "").slice(0, 10) === date);
                  const st = (sd && sd.start_time) || j.start_time;
                  const ft = (sd && sd.finish_time) || j.finish_time;
                  const done = (j.dockets || []).some((dk) => !dk.archived && (dk.visit_date || "").slice(0, 10) === date);
                  const ad = (j.appointment_date || "").slice(0, 10);
                  const pending = (j.pending_dockets || []).some((p) => (p.visit_date || "").slice(0, 10) === date);
                  const legacyAwaiting = !(j.pending_dockets || []).length && j.status === "awaiting_signature" && date === (j.awaiting_signature_date || ad);
                  const dayStatus = done ? "completed"
                    : (pending || legacyAwaiting) ? "awaiting_signature"
                    : (j.status === "in_progress" && date === ad) ? "in_progress"
                    : "scheduled";
                  return (
                  <div key={`${date}-${j.id}`} data-testid={`job-card-${j.id}`} onClick={() => navigate(`/field/job/${j.id}`)} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm cursor-pointer hover:border-navy/30 hover:shadow-md transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-heading font-semibold text-navy">{j.customer_name || "Job"}</p>
                        {j.site_address && <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5" />{j.site_address}</p>}
                      </div>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full capitalize ${JOB_STATUS[dayStatus] || "bg-slate-100 text-slate-600"}`}>{dayStatus.replace("_", " ")}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                      {st && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" />{st}{ft ? ` – ${ft}` : ""}</span>}
                    </div>
                    {j.scope_of_works && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{j.scope_of_works}</p>}
                    <p className="text-xs text-navy font-medium mt-2">Open job →</p>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CalendarView() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();
  const today = iso(new Date());
  useEffect(() => {
    api.get(`/contractor/calendar-events?year=${year}`).then((r) => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  const upcoming = events.filter((e) => e.date >= today);
  const Row = ({ e }) => (
    <div data-testid={`cal-event-${e.date}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${e.type === "rdo" ? "bg-indigo-100 text-indigo-600" : "bg-rose-100 text-rose-600"}`}>
          {e.type === "rdo" ? <CalendarClock className="w-4.5 h-4.5" /> : <PartyPopper className="w-4.5 h-4.5" />}
        </div>
        <div>
          <p className="text-sm font-medium text-navy">{e.name}</p>
          <p className="text-xs text-slate-400">{e.type === "rdo" ? "RDO" : "Public Holiday"}</p>
        </div>
      </div>
      <span className="text-sm text-slate-500">{new Date(e.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="calendar-view">
      <p className="text-sm text-slate-500">Victorian public holidays and construction industry RDOs for {year}.</p>
      {loading ? <p className="text-slate-400 text-sm">Loading…</p> : (
        <>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <p className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-400 font-semibold bg-slate-50">Upcoming</p>
            {upcoming.length === 0 ? <p className="px-4 py-6 text-sm text-slate-400 text-center">Nothing upcoming this year.</p> : upcoming.map((e) => <Row key={e.id} e={e} />)}
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <p className="px-4 py-2.5 text-xs uppercase tracking-wider text-slate-400 font-semibold bg-slate-50">All {year}</p>
            {events.map((e) => <Row key={e.id} e={e} />)}
          </div>
        </>
      )}
    </div>
  );
}

function MyProfile({ data, onSaved }) {
  return (
    <div className="space-y-5" data-testid="my-profile">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm" data-testid="my-rates">
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2 mb-1"><DollarSign className="w-5 h-5" />Your Agreed Rates</h3>
        <p className="text-sm text-slate-500 mb-4">These are the rates you agreed to (not including super — super is paid on top of these rates directly). Contact True Seal if anything looks incorrect.</p>
        <div className="divide-y divide-slate-100">
          {data.rates.map(([label, value], i) => (
            <div key={i} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-slate-600">{label}</span><span className="text-navy font-medium">{value}</span>
            </div>
          ))}
        </div>
      </div>

      <ProfileCard data={data} onSaved={onSaved} />
      <DocumentsCard data={data} onSaved={onSaved} title="Cards & Insurances" subtitle="Keep your compliance documents up to date. Replace any that are expiring." />
      <AgreementCard data={data} />
    </div>
  );
}


function StatusBanner({ data }) {
  if (data.status === "active") return (
    <div data-testid="status-active" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3">
      <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
      <div><p className="font-heading font-semibold text-emerald-800">You're activated</p><p className="text-sm text-emerald-700">Your onboarding is complete and approved. Welcome to the True Seal team.</p></div>
    </div>
  );
  if (data.status === "under_review") return (
    <div data-testid="status-under-review" className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 flex items-start gap-3">
      <Clock className="w-6 h-6 text-indigo-600 shrink-0" />
      <div><p className="font-heading font-semibold text-indigo-800">Under review</p><p className="text-sm text-indigo-700">Thanks — your onboarding has been submitted. True Seal will review and activate your account shortly.</p></div>
    </div>
  );
  if (data.status === "changes_requested") return (
    <div data-testid="status-changes-requested" className="bg-orange-50 border border-orange-200 rounded-2xl p-5 flex items-start gap-3">
      <AlertTriangle className="w-6 h-6 text-orange-600 shrink-0" />
      <div><p className="font-heading font-semibold text-orange-800">Changes requested</p><p className="text-sm text-orange-700">{data.review_note || "Please review and update your details, then re-submit."}</p></div>
    </div>
  );
  return null;
}

function Card({ icon: Icon, title, subtitle, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
      <div className="flex items-center gap-2 mb-1"><Icon className="w-5 h-5 text-navy" /><h3 className="font-heading font-semibold text-navy">{title}</h3></div>
      {subtitle && <p className="text-sm text-slate-500 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function AgreementCard({ data }) {
  return (
    <Card icon={FileSignature} title={`${data.worker_noun || "Contractor"} Agreement`} subtitle="Signed and on file." testid="card-agreement">
      <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
        <p className="flex items-center gap-2 text-sm text-navy font-medium"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Acknowledged & signed</p>
        {data.ack_pdf_available && <a href={agreementUrl()} target="_blank" rel="noreferrer" data-testid="view-signed-agreement" className="inline-flex items-center gap-1 text-blue-700 text-sm font-medium"><FileText className="w-4 h-4" />View</a>}
      </div>
    </Card>
  );
}

function ProfileCard({ data, onSaved }) {
  const [form, setForm] = useState({
    mobile: data.mobile || "",
    profile: { address: "", suburb: "", state: "", postcode: "", email: "", ...(data.profile || {}) },
    emergency_contact: { name: "", relationship: "", phone: "", ...(data.emergency_contact || {}) },
  });
  const [busy, setBusy] = useState(false);
  const setP = (k, v) => setForm((f) => ({ ...f, profile: { ...f.profile, [k]: v } }));
  const setEc = (k, v) => setForm((f) => ({ ...f, emergency_contact: { ...f.emergency_contact, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/contractor/me/profile", form);
      onSaved(r.data); toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Card icon={UserCog} title="Complete Your Profile" subtitle="Confirm your contact and emergency details." testid="card-profile">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Mobile *</Label><Input data-testid="profile-mobile" value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} className="mt-1" /></div>
        <div><Label>Email</Label><Input data-testid="profile-email" type="email" value={form.profile.email} onChange={(e) => setP("email", e.target.value)} className="mt-1" placeholder="Contact email" /></div>
        <div><Label>Residential Address *</Label><Input data-testid="profile-address" value={form.profile.address} onChange={(e) => setP("address", e.target.value)} className="mt-1" /></div>
        <div><Label>Suburb</Label><Input value={form.profile.suburb} onChange={(e) => setP("suburb", e.target.value)} className="mt-1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>State</Label><Input value={form.profile.state} onChange={(e) => setP("state", e.target.value)} className="mt-1" /></div>
          <div><Label>Postcode</Label><Input value={form.profile.postcode} onChange={(e) => setP("postcode", e.target.value)} className="mt-1" /></div>
        </div>
      </div>
      <h4 className="text-xs uppercase tracking-widest font-semibold text-slate-400 mt-5 mb-2">Emergency Contact</h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label>Name *</Label><Input data-testid="profile-ec-name" value={form.emergency_contact.name} onChange={(e) => setEc("name", e.target.value)} className="mt-1" /></div>
        <div><Label>Relationship</Label><Input value={form.emergency_contact.relationship} onChange={(e) => setEc("relationship", e.target.value)} className="mt-1" /></div>
        <div><Label>Phone *</Label><Input data-testid="profile-ec-phone" value={form.emergency_contact.phone} onChange={(e) => setEc("phone", e.target.value)} className="mt-1" /></div>
      </div>
      <Button data-testid="profile-save" onClick={save} disabled={busy} className="mt-4 bg-navy hover:bg-navy-light">{busy ? "Saving…" : "Save Profile"}</Button>
    </Card>
  );
}

function DocumentsCard({ data, onSaved, title = "Required Documents", subtitle = "Upload your compliance documents. Insurances require an expiry date." }) {
  return (
    <Card icon={ClipboardCheck} title={title} subtitle={subtitle} testid="card-documents">
      <div className="space-y-3">
        {data.documents.map((doc) => (
          doc.bank ? <BankRow key={doc.key} doc={doc} onSaved={onSaved} />
            : doc.text ? <AbnRow key={doc.key} doc={doc} onSaved={onSaved} />
            : <DocRow key={doc.key} doc={doc} onSaved={onSaved} />
        ))}
      </div>
    </Card>
  );
}

function DocRow({ doc, onSaved }) {
  const fileRef = useRef(null);
  const [expiry, setExpiry] = useState(doc.expiry || "");
  const [notes, setNotes] = useState(doc.notes || "");
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (doc.requires_expiry && !expiry) { toast.error("Please set the expiry date first."); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("expiry", expiry || ""); fd.append("notes", notes || "");
    try {
      const r = await api.post(`/contractor/me/documents/${doc.key}`, fd);
      onSaved(r.data); toast.success(`${doc.label} uploaded`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveMeta = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/contractor/me/documents/${doc.key}`, { expiry, notes });
      onSaved(r.data); toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`doc-${doc.key}`} className={`rounded-xl border p-4 ${doc.complete ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {doc.complete ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
          <div>
            <p className="font-medium text-navy text-sm">{doc.label}</p>
            {doc.filename && <p className="text-xs text-slate-500">{doc.filename}</p>}
          </div>
        </div>
        {doc.filename && <a href={authFileUrl(doc.key)} target="_blank" rel="noreferrer" data-testid={`doc-view-${doc.key}`} className="inline-flex items-center gap-1 text-blue-700 text-xs font-medium"><FileText className="w-3.5 h-3.5" />View</a>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        {docHasExpiry(doc) && (
          <div><Label className="text-xs">Expiry Date {doc.complete ? "" : "*"}</Label>
            <Input data-testid={`doc-expiry-${doc.key}`} type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1" /></div>
        )}
        <div><Label className="text-xs">Notes</Label>
          <Input data-testid={`doc-notes-${doc.key}`} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" placeholder="Optional" /></div>
      </div>
      <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`doc-file-${doc.key}`} />
      <div className="flex gap-2 mt-3">
        <Button data-testid={`doc-upload-${doc.key}`} onClick={() => fileRef.current?.click()} variant="outline" size="sm" disabled={busy}><Upload className="w-4 h-4 mr-1.5" />{doc.filename ? "Replace" : "Upload"}</Button>
        {doc.filename && <Button data-testid={`doc-savemeta-${doc.key}`} onClick={saveMeta} variant="ghost" size="sm" disabled={busy}>Save details</Button>}
      </div>
    </div>
  );
}

function docHasExpiry(doc) { return doc.requires_expiry === true; }

function AbnRow({ doc, onSaved }) {
  const [value, setValue] = useState(doc.value || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/contractor/me/documents/${doc.key}`, { value });
      onSaved(r.data); toast.success(`${doc.label} saved`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`doc-${doc.key}`} className={`rounded-xl border p-4 ${doc.complete ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}>
      <div className="flex items-center gap-2">
        {doc.complete ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
        <p className="font-medium text-navy text-sm">{doc.label}</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <Input data-testid="doc-abn-input" value={value} onChange={(e) => setValue(e.target.value)} placeholder={doc.key === "tfn" ? "e.g. 123 456 789" : "e.g. 12 345 678 901"} className="flex-1" />
        <Button data-testid="doc-abn-save" onClick={save} disabled={busy} className="bg-navy hover:bg-navy-light">Save</Button>
      </div>
    </div>
  );
}

function BankRow({ doc, onSaved }) {
  const v = doc.value && typeof doc.value === "object" ? doc.value : {};
  const [form, setForm] = useState({ account_name: v.account_name || "", bsb: v.bsb || "", account_number: v.account_number || "" });
  const [busy, setBusy] = useState(false);
  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/contractor/me/documents/${doc.key}`, { value: form });
      onSaved(r.data); toast.success(`${doc.label} saved`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`doc-${doc.key}`} className={`rounded-xl border p-4 ${doc.complete ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"}`}>
      <div className="flex items-center gap-2">
        {doc.complete ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Circle className="w-5 h-5 text-slate-300" />}
        <p className="font-medium text-navy text-sm">{doc.label}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <div className="sm:col-span-3"><Label className="text-xs">Account Name</Label>
          <Input data-testid="bank-account-name" value={form.account_name} onChange={(e) => set("account_name", e.target.value)} className="mt-1" /></div>
        <div><Label className="text-xs">BSB</Label>
          <Input data-testid="bank-bsb" value={form.bsb} onChange={(e) => set("bsb", e.target.value)} placeholder="000-000" className="mt-1" /></div>
        <div className="sm:col-span-2"><Label className="text-xs">Account Number</Label>
          <Input data-testid="bank-account-number" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} className="mt-1" /></div>
      </div>
      <Button data-testid="bank-save" onClick={save} disabled={busy} className="mt-3 bg-navy hover:bg-navy-light">Save Bank Details</Button>
    </div>
  );
}

function SubmitCard({ data, onSaved }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/contractor/me/submit");
      onSaved(r.data); toast.success("Submitted for review");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (data.status === "under_review" || data.status === "active") return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm text-center">
      <p className="text-sm text-slate-500 mb-3">{data.can_submit ? "Everything's complete. Submit your onboarding for True Seal to review." : "Complete your profile and all required documents to submit for review."}</p>
      <Button data-testid="contractor-submit" onClick={submit} disabled={busy || !data.can_submit} className="bg-navy hover:bg-navy-light py-6 px-8 text-base"><Send className="w-5 h-5 mr-2" />{busy ? "Submitting…" : "Submit for Review"}</Button>
    </div>
  );
}
